import axios, { AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '@/features/auth/store';
import type { AuthUser, AuthChambers } from '@/features/auth/types';
import { useClerkWorkspaceStore } from '@/lib/clerkWorkspaceStore';
import { signOutWithNotice } from './signOutNotice';

/**
 * In dev: VITE_API_URL is empty → baseURL becomes "/api/v1" (relative).
 *         The Vite dev server proxies /api → http://localhost:4000.
 *         No CORS, no cross-origin headache.
 *
 * In prod: VITE_API_URL is the full API origin (e.g. https://api.jurivoxa.app).
 */
const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    // CSRF guard on cookie-auth refresh/logout requires this header.
    // Cross-site forms cannot set it; cross-origin fetches trigger
    // a CORS preflight that our allowlist rejects.
    'X-Requested-With': 'jurivoxa-web',
  },
});

// Attach Authorization header + prevent browser HTTP caching on all requests.
// Without Cache-Control: no-store, the browser caches GET responses with ETags
// and returns 304 Not Modified on subsequent polls — meaning the UI never sees
// new data even when the server has it. This kills 304s globally.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const activeBarristerId = useClerkWorkspaceStore.getState().activeBarristerId;
  if (activeBarristerId) {
    config.headers['X-Active-Barrister'] = activeBarristerId;
  }
  // FormData requests must NOT have Content-Type set — the browser sets it
  // automatically with the multipart boundary. If we leave the global
  // 'application/json' default in place, multer cannot parse the file.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  if (config.method === 'get' || config.method === 'GET' || !config.method) {
    config.headers['Cache-Control'] = 'no-store';
    config.headers['Pragma'] = 'no-cache';
  }
  return config;
});

// Track in-flight refresh to coalesce concurrent 401s
let refreshPromise: Promise<string> | null = null;

// Once a refresh has definitively failed, stop trying. Every subsequent
// 401 short-circuits to reject — otherwise polling components (notifications,
// awaiting tray, briefs inbox, etc.) keep firing fresh refresh attempts
// during the brief window before the browser finishes reloading to /login.
//
// Cleared automatically when a fresh access token is set (e.g. after login),
// so the same browser session can recover without a hard reload.
let authFailed = false;
useAuthStore.subscribe((state, prev) => {
  if (state.accessToken && state.accessToken !== prev.accessToken) {
    authFailed = false;
  }
});

async function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = api
    .post('/auth/refresh', {})
    .then((res) => {
      const data = res.data?.data as {
        accessToken: string;
        user?: AuthUser;
        chambers?: AuthChambers;
        permissions?: string[];
      };
      const { accessToken, user, chambers, permissions } = data;
      if (user && chambers && permissions) {
        useAuthStore.getState().setSession({ accessToken, user, chambers, permissions });
      } else {
        useAuthStore.getState().setAccessToken(accessToken);
      }
      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * One-shot session bootstrap for a cold app load that has NO persisted user but
 * MAY still hold a valid httpOnly refresh cookie (e.g. a barrister opening a
 * deep link from an email on a browser whose localStorage was evicted — Safari
 * ITP, privacy settings — while the session cookie survives).
 *
 * The 401-interceptor only refreshes when an authenticated API call is actually
 * made; with no persisted user the route guard would otherwise bounce straight
 * to /login without ever trying the cookie. This exchanges the cookie for a full
 * session up front. Resolves `true` if a user session was established, else `false`.
 * Never throws — a failed/absent cookie simply yields `false`.
 */
export async function bootstrapAuth(): Promise<boolean> {
  if (authFailed) return false;
  try {
    await doRefresh();
    return !!useAuthStore.getState().user;
  } catch {
    return false;
  }
}

// Auto-refresh on 401 (once)
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (authFailed) return Promise.reject(error);

    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;

    // The signed-in user's own account was deactivated (CLAUDE.md #25). Refreshing cannot help — every
    // session was revoked — so sign this tab out now and explain why on the login page, instead of
    // dropping to an unexplained login screen. Sent only in reply to this user's own valid token.
    const authCode = (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
    if (error.response?.status === 401 && authCode === 'ACCOUNT_DEACTIVATED') {
      authFailed = true;
      signOutWithNotice('account_deactivated', () => useAuthStore.getState().clear());
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh') &&
      // AUTHENTICATION ENDPOINTS ARE NOT EXPIRED SESSIONS.
      //
      // A 401 from /mfa/verify means "that code was wrong", not "your token aged out" — the
      // caller has no session yet, only a challenge token. Refreshing on it made a mistyped
      // TOTP fire /auth/refresh, which answered 401 "No refresh token", and THAT became the
      // message on screen: a barrister who fat-fingered one digit was told the app had lost
      // their session. It also latched `authFailed`, so the client stopped retrying anything
      // for the rest of the page's life. Found by typing a wrong code in a real browser.
      !original.url?.includes('/mfa/verify')
    ) {
      original._retry = true;
      try {
        const newToken = await doRefresh();
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        return api.request(original);
      } catch (retryErr: unknown) {
        const retryStatus = (retryErr as import('axios').AxiosError)?.response?.status;
        // Only force-logout when the retry itself returns 401 (token truly dead)
        // or when no response arrived (network failure during refresh).
        // A 5xx from the retry is a server error — not an auth failure.
        if (!retryStatus || retryStatus === 401) {
          authFailed = true;
          useAuthStore.getState().clear();
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.assign('/login');
          }
        }
        return Promise.reject(retryErr);
      }
    }

    // Global 5xx handler — surfaces unexpected server errors so they are never silently swallowed.
    // Excludes /stream and /download endpoints: those components show their own error UI.
    const reqUrl = original?.url ?? '';
    const silentEndpoint = reqUrl.includes('/stream') || reqUrl.includes('/download');
    if (error.response && error.response.status >= 500 && !silentEndpoint) {
      import('sonner').then(({ toast }) => {
        toast.error('A server error occurred. Please try again or contact support if it persists.', {
          id: 'global-500-error'
        });
      });
    }

    // Trial read-only lockout: a write was blocked because the free trial has ended. Explain it
    // (the write silently failing would be confusing) and point at the upgrade action in the banner.
    const code = (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
    if (error.response?.status === 402 && code === 'TRIAL_EXPIRED') {
      import('sonner').then(({ toast }) => {
        toast.error('Your free trial has ended — upgrade to make changes. You still have read-only access.');
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Read the API's `{ error: { code, message, requestId } }` envelope off an Axios error.
 *
 * ── WHY THIS IS NOT JUST `err.response.data.error` ─────────────────────────────────────────
 * When a request sets `responseType: 'arraybuffer'` (or `'blob'`), Axios applies that type to
 * the ERROR response too. So `err.response.data` is a Uint8Array/Blob holding the JSON bytes,
 * and `data.error.code` is `undefined` — silently, for every binary-typed request.
 *
 * That is not hypothetical. DocumentPreviewModal loads documents with
 * `responseType: 'arraybuffer'` and branched on `data.error.code` to tell a STORAGE OUTAGE
 * ("your file is safe, try again shortly") apart from a GENUINELY MISSING OBJECT ("this needs
 * restoring"). The code was always `undefined`, so every document failure — outage, missing
 * file, permission denial — fell through to the same generic "it may have been removed, or you
 * may not have permission" message. Found by opening the page in a browser; no test caught it,
 * because every test asserted on the server's response, which was correct all along.
 *
 * `requestId` is read from the body first and from the `x-request-id` response header second.
 * The header is the more robust source (it survives a non-JSON body) and is exposed to the
 * browser via the CORS `exposedHeaders` allowlist in the API's app.ts.
 */
export interface ApiErrorInfo {
  status: number | null;
  code: string | null;
  message: string | null;
  requestId: string | null;
}

type ErrorBody = {
  error?: { code?: string; message?: string; requestId?: string; details?: Record<string, string[] | null> };
};

function decodeErrorBody(data: unknown): ErrorBody | null {
  if (!data) return null;
  if (typeof data === 'object' && !ArrayBuffer.isView(data) && !(data instanceof ArrayBuffer)) {
    return data as ErrorBody;
  }
  try {
    const bytes =
      data instanceof ArrayBuffer ? new Uint8Array(data)
      : ArrayBuffer.isView(data) ? new Uint8Array((data as ArrayBufferView).buffer)
      : null;
    if (!bytes) return null;
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    // A binary body that is not our JSON envelope. Nothing to read — say so rather than guess.
    return null;
  }
}

export function normaliseApiError(err: unknown): ApiErrorInfo {
  if (!axios.isAxiosError(err)) {
    return { status: null, code: null, message: err instanceof Error ? err.message : null, requestId: null };
  }
  const res = err.response;
  const body = decodeErrorBody(res?.data);
  const header = res?.headers?.['x-request-id'];
  return {
    status: res?.status ?? null,
    code: body?.error?.code ?? null,
    message: body?.error?.message ?? null,
    requestId: body?.error?.requestId ?? (typeof header === 'string' ? header : null),
  };
}

/**
 * Was this request REFUSED (403) rather than failing?
 *
 * A refusal is a decision about the caller, not a fault, and the server's wording names
 * internal member types ("Route restricted to: admin, senior_clerk, …") which means nothing to
 * the person reading it. Call sites use this to say plainly that something is unavailable for
 * their role, instead of printing the server's own sentence at them.
 */
export function isForbidden(err: unknown): boolean {
  return normaliseApiError(err).status === 403;
}

/**
 * Helper: extract a usable error message from an Axios error.
 */
/**
 * The per-field messages behind a 400 VALIDATION_ERROR, in the order the server listed them.
 * Empty for every other kind of failure, so callers can fall back to the plain message.
 */
export function validationFieldMessages(err: unknown): string[] {
  return Object.values(validationFieldErrors(err));
}

/** The same per-field messages keyed by field name, for marking the boxes themselves. */
export function validationFieldErrors(err: unknown): Record<string, string> {
  if (!axios.isAxiosError(err)) return {};
  const body = decodeErrorBody(err.response?.data);
  if (body?.error?.code !== 'VALIDATION_ERROR') return {};
  const out: Record<string, string> = {};
  for (const [field, msgs] of Object.entries(body.error.details ?? {})) {
    const first = (msgs ?? []).find((m) => typeof m === 'string' && m.trim().length > 0);
    if (first) out[field] = first;
  }
  return out;
}

export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ERR_NETWORK' || !err.response) {
      return fallback !== 'Something went wrong'
        ? `${fallback} — the server could not be reached. Please try again.`
        : 'Server unreachable. Please try again shortly.';
    }
    // A Zod refusal arrives as the generic sentence "Validation failed" plus a per-field map in
    // `details` — and the map is where the usable wording is ("Sort code must be 6 digits,
    // e.g. 12-34-56"). Showing only the sentence left people staring at "Validation failed" on
    // the bank-account form with no idea which box was wrong, so prefer the field messages.
    const fieldMessages = validationFieldMessages(err);
    if (fieldMessages.length) return fieldMessages.join(' · ');
    // Decoded, so a binary-typed request gets the real message instead of `err.message`.
    return normaliseApiError(err).message ?? err.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
